import { useEffect, useState } from 'react'
import {
  TEMPLATE_SCOPE_HINT,
  TEMPLATE_SCOPE_LABEL,
  type MeDto,
  type TemplateContent,
  type TemplateDto,
  type TemplateScope,
} from '@praesto/shared'
import { api, ApiError } from '../api.js'
import { Dialog } from './Dialog.js'
import { useText } from '../i18n.js'

/**
 * Vorlagen-Auswahl.
 *
 * Die Kacheln zeigen einen Grundriss, kein Abbild: rechteckige Platzhalter aus
 * den Maßen der Elemente. Ein echtes Vorschaubild hieße, die Mediendateien der
 * Vorlage auszuliefern — und die gehören unter Umständen einer anderen
 * Organisation als der, die gerade schaut. Der Grundriss verrät die Anordnung
 * und sonst nichts.
 */
export function TemplatesDialog({
  orgId,
  onApply,
  onClose,
}: {
  orgId: string
  onApply: (template: TemplateDto) => Promise<void>
  onClose: () => void
}): React.ReactElement {
  const t = useText()
  const [templates, setTemplates] = useState<TemplateDto[] | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .templates(orgId)
      .then(setTemplates)
      .catch(() => setError(t('templates.loadFailed')))
  }, [orgId])

  async function use(template: TemplateDto): Promise<void> {
    setError(null)
    setBusyId(template.id)
    try {
      await onApply(template)
      onClose()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('templates.applyFailed'))
    } finally {
      setBusyId(null)
    }
  }

  async function remove(template: TemplateDto): Promise<void> {
    await api.deleteTemplate(template.id)
    setTemplates((current) => (current ?? []).filter((t) => t.id !== template.id))
  }

  const groups: { scope: TemplateScope; items: TemplateDto[] }[] = (
    ['personal', 'organisation', 'platform'] as const
  )
    .map((scope) => ({ scope, items: (templates ?? []).filter((t) => t.scope === scope) }))
    .filter((group) => group.items.length > 0)

  return (
    <Dialog title={t('templates.applyTitle')} onClose={onClose}>
      {templates === null && !error && <p className="muted small">{t('common.loading')}</p>}
      {templates?.length === 0 && (
        <p className="muted small">
          Noch keine Vorlage. Wähle auf dem Board einen Frame aus und sichere ihn über „Als
          Vorlage“ — mit allem, was darin liegt.
        </p>
      )}

      {groups.map((group) => (
        <section key={group.scope}>
          <h4 className="dialog-section">{t(TEMPLATE_SCOPE_LABEL[group.scope])}</h4>
          <ul className="template-grid">
            {group.items.map((template) => (
              <li key={template.id}>
                <button
                  className="template-card"
                  onClick={() => void use(template)}
                  disabled={busyId !== null}
                >
                  <TemplateSketch content={template.content} />
                  <span className="template-name">{template.name}</span>
                  <span className="template-meta muted small">
                    {t.plural(
                      template.content.elements.length,
                      'templates.elements.one',
                      'templates.elements.many',
                    )}
                    {' · '}
                    {template.authorName}
                  </span>
                </button>
                {template.canManage && (
                  <button
                    className="button ghost icon template-remove"
                    onClick={() => void remove(template)}
                    aria-label={t('templates.deleteLabel', { name: template.name })}
                    title={t('templates.delete')}
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}

      {busyId && <p className="muted small">{t('templates.copying')}</p>}
      {error && <p className="form-error">{error}</p>}
    </Dialog>
  )
}

/** Frame als Vorlage sichern. */
export function SaveTemplateDialog({
  boardId,
  frameName,
  content,
  me,
  onClose,
  onSaved,
}: {
  boardId: string
  frameName: string
  content: TemplateContent
  me: MeDto
  onClose: () => void
  onSaved: (template: TemplateDto) => void
}): React.ReactElement {
  const t = useText()
  const [name, setName] = useState(frameName)
  const [scope, setScope] = useState<TemplateScope>('personal')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const scopes: TemplateScope[] = me.isPlatformAdmin
    ? ['personal', 'organisation', 'platform']
    : ['personal', 'organisation']

  async function save(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const template = await api.createTemplate({ boardId, name: name.trim(), scope, content })
      onSaved(template)
      onClose()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('templates.saveFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog title={t('templates.saveTitle')} onClose={onClose}>
      <p className="muted small">
        Der Frame wird mit allem, was in ihm liegt, kopiert — Bilder und Videos eingeschlossen.
        Spätere Änderungen an diesem Board wirken nicht auf die Vorlage zurück.
      </p>

      <form onSubmit={save}>
        <label className="field">
          <span>{t('common.name')}</span>
          <input value={name} onChange={(event) => setName(event.target.value)} required autoFocus />
        </label>

        <fieldset className="mode-choice">
          <legend className="sr-only">{t('templates.scope')}</legend>
          {scopes.map((candidate) => (
            <label key={candidate} className={scope === candidate ? 'is-selected' : undefined}>
              <input
                type="radio"
                name="template-scope"
                value={candidate}
                checked={scope === candidate}
                onChange={() => setScope(candidate)}
              />
              <span className="mode-body">
                <span className="mode-label">{t(TEMPLATE_SCOPE_LABEL[candidate])}</span>
                <span className="mode-description">{t(TEMPLATE_SCOPE_HINT[candidate])}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <p className="muted small">
          {t.plural(
            content.elements.length,
            'templates.saveCount.one',
            'templates.saveCount.many',
          )}
        </p>

        <button className="button primary" type="submit" disabled={busy}>
          {busy ? t('templates.saving') : t('templates.save')}
        </button>
        {error && <p className="form-error">{error}</p>}
      </form>
    </Dialog>
  )
}

/** Grundriss einer Vorlage: Anordnung ja, Inhalt nein. */
function TemplateSketch({ content }: { content: TemplateContent }): React.ReactElement {
  return (
    <svg
      className="template-sketch"
      viewBox={`0 0 ${content.width} ${content.height}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      <rect
        x={0}
        y={0}
        width={content.width}
        height={content.height}
        fill={content.background || 'transparent'}
        stroke={content.borderWidth > 0 ? content.borderColor || 'currentColor' : 'none'}
        strokeWidth={content.borderWidth}
      />
      {content.elements.map((element) => (
        <rect
          key={element.id}
          className={`sketch-${element.type}`}
          x={element.x}
          y={element.y}
          width={element.w}
          height={element.h}
          rx={Math.min(element.w, element.h) * 0.03}
        />
      ))}
    </svg>
  )
}
